import { create } from 'apisauce';

import { CLIENT_PAGES } from '~constants/pages';
import { RENDER_CALL_CENTER } from '~constants/environment';
import LocalStorageService from '~services/LocalStorageService';

import { errorNormalizer } from './apiTransforms';

export const TOKEN_TYPE = 'Bearer';

const CALL_CENTER = '/call_center/';

const STATUS_CODES = {
  unauthorized: 401,
  unavailable: 503
};

// Several containers request the same endpoint at the same time — Routes fetches
// on app mount while Home fetches again as soon as its lazy chunk lands — which
// doubled the API waterfall sitting in front of the LCP image. Concurrent
// identical GETs now share one request. The entry is dropped as soon as the
// request settles, so this is de-duplication, never a cache: a later call always
// hits the network again. Only GETs are affected, and each caller gets its own
// response wrapper so nobody sees another caller's mutations of the top level.
const inFlightGets = new Map();

const dedupeConcurrentGets = instance => {
  const originalGet = instance.get;

  instance.get = (url, params, axiosConfig) => {
    // axiosConfig can carry cancel tokens or per-call headers, so requests that
    // pass one are never shared.
    const key = JSON.stringify([url, params, axiosConfig]);
    const pending = inFlightGets.get(key);

    if (pending) {
      return pending.then(response => ({ ...response }));
    }

    const request = originalGet.call(instance, url, params, axiosConfig);
    const forget = () => {
      if (inFlightGets.get(key) === request) {
        inFlightGets.delete(key);
      }
    };
    // Both handlers are attached so a rejection here is never unhandled; the
    // promise handed to callers keeps rejecting as it did before.
    request.then(forget, forget);
    inFlightGets.set(key, request);

    return request.then(response => ({ ...response }));
  };

  return instance;
};

const api = dedupeConcurrentGets(
  create({
    baseURL: process.env.REACT_APP_CORE_HOST,
    headers: { 'X-PLATFORM': 'web' }
  })
);

export const callCenterApi = dedupeConcurrentGets(
  create({
    baseURL: process.env.REACT_APP_CORE_HOST,
    headers: { 'X-PLATFORM': 'call_center' }
  })
);

export const getApi = () => (RENDER_CALL_CENTER ? callCenterApi : api);

export const socialLoginApi = create({
  baseURL: process.env.REACT_APP_SOCIAL_URL,
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
});

export const helpCenterApi = create({
  baseURL: process.env.REACT_APP_ZENDESK_URL
});

export const contactFormApi = create({
  baseURL: process.env.REACT_APP_CONTACT_URL
});

export const setTokenHeader = token => {
  const authToken = `${TOKEN_TYPE} ${token}`;
  api.setHeaders({
    Authorization: authToken
  });
  LocalStorageService.setAuthToken(authToken);
};

// eslint-disable-next-line no-unused-vars
export const apiSetup = dispatch => {
  api.addResponseTransform(errorNormalizer);

  api.addMonitor(response => {
    if (response.status === STATUS_CODES.unavailable && location.pathname !== CLIENT_PAGES.UNAVAILABLE.path) {
      location.pathname = CLIENT_PAGES.UNAVAILABLE.path;
    }
  });

  api.addRequestTransform(request => {
    if (request?.url.includes(CALL_CENTER)) {
      api.setHeader('X-PLATFORM', 'call_center');

      if (request?.url.includes('#')) {
        const { url } = request;
        const splitUrl = url.split('#');
        request.headers['X-USERID'] = splitUrl[1];
        request.url = splitUrl[0];
      }
    }
  });
};

export const cdnApi = create({
  baseURL: process.env.REACT_APP_PUBLIC_CDN
});

export const getApiWithExternalUserId = externalUserId => {
  const externalUserIdApi = create({
    baseURL: process.env.REACT_APP_CORE_HOST,
    headers: { 'X-PLATFORM': 'web' }
  });
  externalUserIdApi.setHeaders({ 'X-USERID': externalUserId?.externalUserId });
  return externalUserIdApi;
};

export default api;
